import React from "react"
import { useTranslation } from "react-i18next"

import { PerformanceCell } from "@/components/performance"
import { useUnit } from "@/components/unit/use-unit"
import { useAuthentication } from "@/contexts"
import { useFormatDecimal } from "@/formatters"
import type { Department } from "@/models/department"
import { UnitName } from "@/models/unit"
import { DataGridColumnHeader } from "@/nzds/data-grid"

import { Stack } from "@mui/material"
import type {
  GridColDef,
  GridColumnVisibilityModel,
  GridPinnedColumnFields,
  GridRenderCellParams,
  GridRowId,
  GridRowModesModel,
  GridSortModel,
} from "@mui/x-data-grid-pro"

import { baseSiteNumberColumnDef } from "../../../../models"
import type { SiteExplorerGridRecord } from "../../../../services/use-get-site-explorer"
import { useCommonSiteExplorerGridColumns } from "../../../utils/use-common-site-explorer-grid-columns/use-common-site-explorer-grid-columns"
import { useCommonSiteGridColumns } from "../../../utils/use-common-site-grid-columns"
import { PerformanceTooltipLegend } from "../../components/performance-tooltip-legend/performance-tooltip-legend"

export const defaultColumnVisibilityModel: GridColumnVisibilityModel = {
  // default columns
  benchmark_electric_eui_percentile: true,
  benchmark_gas_eui_percentile: true,
  benchmark_majorfuel_eui_percentile: true,
  // non-default columns
  benchmark_electric_eui_median: false, // Electricity Median
  benchmark_gas_eui_median: false, // Natural Gas Median
  benchmark_majorfuel_eui_median: false, // Combined Median
  electricity_kbtu_per_sq_ft: false, // Electricity EUI
  electricity_mt_co2e: false, // Electricity Emissions
  nat_gas_kbtu_per_sq_ft: false, // Natural Gas EUI
  nat_gas_mt_co2e: false, // Natural Gas Emissions
  total_emissions_mt_co2e: false, // Combined Emissions
}

export const defaultPinnedColumns: GridPinnedColumnFields = {
  left: ["rowNumber", "site_name"],
  right: ["actions"],
}

export const defaultSortModel: GridSortModel = [
  {
    field: "site_name",
    sort: "desc",
  },
]

interface UsePerformanceGridColumnsProps {
  buildingTypes: string[] | undefined
  departments: Department[] | undefined
  handleRowEditCancelClick: (id: GridRowId) => void
  handleRowEditClick: (id: GridRowId) => void
  handleRowEditSaveClick: (id: GridRowId) => void
  rowModesModel: GridRowModesModel
}

export const usePerformanceGridColumns = (
  props: UsePerformanceGridColumnsProps
): GridColDef<SiteExplorerGridRecord>[] => {
  const { t } = useTranslation()
  const { canEditSite } = useAuthentication()

  const { formatDecimal: formatEUI } = useFormatDecimal(
    {
      precision: 2,
    },
    {
      showUnit: false,
      unit: UnitName.KiloBritishThermalUnitPerSquareFoot,
    }
  )

  const kbtuPerSqFtUnit = useUnit(
    UnitName.KiloBritishThermalUnitPerSquareFoot,
    null
  )

  const commonSiteGridColumns =
    useCommonSiteGridColumns<SiteExplorerGridRecord>()
  const commonSiteExplorerGridColumns = useCommonSiteExplorerGridColumns(props)

  const columns: GridColDef<SiteExplorerGridRecord>[] = React.useMemo(() => {
    return [
      {
        ...commonSiteGridColumns.rowNumber,
      },
      {
        ...commonSiteGridColumns.siteName,
      },
      {
        ...commonSiteExplorerGridColumns.group,
      },
      {
        ...commonSiteExplorerGridColumns.buildingType,
      },
      {
        ...commonSiteExplorerGridColumns.buildingArea,
      },
      {
        ...commonSiteExplorerGridColumns.combinedEnergyUsage,
      },
      {
        ...commonSiteExplorerGridColumns.combinedEmissions,
      },
      {
        ...commonSiteExplorerGridColumns.combinedEui,
      },
      {
        ...baseSiteNumberColumnDef,
        field: "benchmark_majorfuel_eui_median",
        headerName: t("gridColumn.combinedEuiBenchmark.name", { ns: "common" }),
        renderHeader: () => (
          <DataGridColumnHeader
            headerName={t("gridColumn.combinedEuiBenchmark.name", {
              ns: "common",
            })}
            tooltipContent={t("gridColumn.combinedEuiBenchmark.description", {
              ns: "common",
            })}
            unit={kbtuPerSqFtUnit}
          />
        ),
        sortable: false,
        valueFormatter: (
          value: SiteExplorerGridRecord["benchmark_majorfuel_eui_median"]
        ) => formatEUI(value),
        width: 240,
      },
      {
        ...baseSiteNumberColumnDef,
        display: "flex",
        field: "benchmark_majorfuel_eui_percentile",
        headerName: t("gridColumn.combinedPerformance.name", { ns: "common" }),
        renderHeader: () => (
          <DataGridColumnHeader
            headerName={t("gridColumn.combinedPerformance.name", {
              ns: "common",
            })}
            tooltipContent={
              <Stack
                sx={{
                  gap: 1,
                }}
              >
                {t("gridColumn.combinedPerformance.description", {
                  ns: "common",
                })}
                <PerformanceTooltipLegend />
              </Stack>
            }
            unit={t("performance.unit", { ns: "sites" })}
          />
        ),
        renderCell: (
          params: GridRenderCellParams<SiteExplorerGridRecord, Nullable<string>>
        ) => <PerformanceCell value={Number.parseFloat(params.value ?? "")} />,
        sortable: false,
        width: 240,
      },
      {
        ...commonSiteExplorerGridColumns.electricityUsage,
      },
      {
        ...commonSiteExplorerGridColumns.electricityEmissions,
      },
      {
        ...commonSiteExplorerGridColumns.electricityEui,
      },
      {
        ...baseSiteNumberColumnDef,
        field: "benchmark_electric_eui_median",
        headerName: t("gridColumn.electricityEuiBenchmark.name", {
          ns: "common",
        }),
        renderHeader: () => (
          <DataGridColumnHeader
            headerName={t("gridColumn.electricityEuiBenchmark.name", {
              ns: "common",
            })}
            tooltipContent={t(
              "gridColumn.electricityEuiBenchmark.description",
              {
                ns: "common",
              }
            )}
            unit={kbtuPerSqFtUnit}
          />
        ),
        sortable: false,
        valueFormatter: (
          value: SiteExplorerGridRecord["benchmark_electric_eui_median"]
        ) => formatEUI(value),
        width: 260,
      },
      {
        ...baseSiteNumberColumnDef,
        display: "flex",
        field: "benchmark_electric_eui_percentile",
        headerName: t("gridColumn.electricityPerformance.name", {
          ns: "common",
        }),
        renderHeader: () => (
          <DataGridColumnHeader
            headerName={t("gridColumn.electricityPerformance.name", {
              ns: "common",
            })}
            tooltipContent={
              <Stack
                sx={{
                  gap: 1,
                }}
              >
                {t("gridColumn.electricityPerformance.description", {
                  ns: "common",
                })}
                <PerformanceTooltipLegend />
              </Stack>
            }
            unit={t("performance.unit", { ns: "sites" })}
          />
        ),
        renderCell: (
          params: GridRenderCellParams<SiteExplorerGridRecord, Nullable<string>>
        ) => <PerformanceCell value={Number.parseFloat(params.value ?? "")} />,
        sortable: false,
        width: 240,
      },
      {
        ...commonSiteExplorerGridColumns.naturalGasUsage,
      },
      {
        ...commonSiteExplorerGridColumns.naturalGasEmissions,
      },
      {
        ...commonSiteExplorerGridColumns.naturalGasEui,
      },
      {
        ...baseSiteNumberColumnDef,
        field: "benchmark_gas_eui_median",
        headerName: t("gridColumn.naturalGasEuiBenchmark.name", {
          ns: "common",
        }),
        renderHeader: () => (
          <DataGridColumnHeader
            headerName={t("gridColumn.naturalGasEuiBenchmark.name", {
              ns: "common",
            })}
            tooltipContent={t("gridColumn.naturalGasEuiBenchmark.description", {
              ns: "common",
            })}
            unit={kbtuPerSqFtUnit}
          />
        ),
        valueFormatter: (
          value: SiteExplorerGridRecord["benchmark_gas_eui_median"]
        ) => formatEUI(value),
        sortable: false,
        width: 260,
      },
      {
        ...baseSiteNumberColumnDef,
        display: "flex",
        field: "benchmark_gas_eui_percentile",
        headerName: t("gridColumn.naturalGasPerformance.name", {
          ns: "common",
        }),
        renderHeader: () => (
          <DataGridColumnHeader
            headerName={t("gridColumn.naturalGasPerformance.name", {
              ns: "common",
            })}
            tooltipContent={
              <Stack
                sx={{
                  gap: 1,
                }}
              >
                {t("gridColumn.naturalGasPerformance.description", {
                  ns: "common",
                })}
                <PerformanceTooltipLegend />
              </Stack>
            }
            unit={t("performance.unit", { ns: "sites" })}
          />
        ),
        renderCell: (
          params: GridRenderCellParams<SiteExplorerGridRecord, Nullable<string>>
        ) => <PerformanceCell value={Number.parseFloat(params.value ?? "")} />,
        sortable: false,
        width: 240,
      },
      ...(canEditSite
        ? [
            {
              ...commonSiteExplorerGridColumns.actions,
            },
          ]
        : []),
    ]
  }, [
    canEditSite,
    commonSiteGridColumns.rowNumber,
    commonSiteGridColumns.siteName,
    commonSiteExplorerGridColumns.group,
    commonSiteExplorerGridColumns.buildingType,
    commonSiteExplorerGridColumns.buildingArea,
    commonSiteExplorerGridColumns.combinedEnergyUsage,
    commonSiteExplorerGridColumns.combinedEmissions,
    commonSiteExplorerGridColumns.combinedEui,
    commonSiteExplorerGridColumns.electricityUsage,
    commonSiteExplorerGridColumns.electricityEmissions,
    commonSiteExplorerGridColumns.electricityEui,
    commonSiteExplorerGridColumns.naturalGasUsage,
    commonSiteExplorerGridColumns.naturalGasEmissions,
    commonSiteExplorerGridColumns.naturalGasEui,
    commonSiteExplorerGridColumns.actions,
    formatEUI,
    kbtuPerSqFtUnit,
    t,
  ])

  return columns
}
